import { queryOptions } from "@tanstack/react-query";
import {
  GET_LIVE_PRESENT_MENU_SUMMARY,
  GET_POINT,
  GET_RTM_LOGIN_AUTH,
  GET_SUPER_CHAT_MENU_LIST,
  GET_UNCOMPLETED_LOVENSE_LOG_BY_ROOM,
  PAY_SECOND_LIVE_POINT,
  SECOND_APPS_GET_LIVE_CHANNELS,
  SEND_MESSAGE_FROM_WEB,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { getCurrentApplicationId } from "~/lib/applicationId";
import { getAvatarUrl, getLiveThumbnailUrl } from "~/lib/imageUrl";
import { queryClient } from "~/lib/queryClient";
import { queryKeys } from "~/lib/queryKeys";
import type {
  GetLivePresentMenuSummaryResponse,
  GetPointResponse,
  GetSuperChatMenuListResponse,
  GetUncompletedLovenseLogResponse,
  LiveChannel,
  LiveChannelsResponse,
  PayLivePointRequest,
  PayLivePointResponse,
  RtmLoginAuthResponse,
  SecondAppsLiveChannelsApiResponse,
} from "./types";

const toChannel = (
  raw: SecondAppsLiveChannelsApiResponse["in_live_list"][number],
  isStandby: boolean,
): LiveChannel => {
  const thumbnailId =
    raw.channel_info.custom_thumbnail_id ||
    raw.channel_info.thumbnail_image_id ||
    null;
  // recording_id を優先し、なければ buzz_live_recording_id を buzz_file_id として使用
  const recordingId = raw.channel_info.recording_id || null;
  const buzzFileId =
    !recordingId && raw.broadcaster.buzz_live_recording_id
      ? raw.broadcaster.buzz_live_recording_id
      : null;
  return {
    channel_id: raw.channel_info.channel_id,
    broadcaster_id: raw.broadcaster.user_id,
    broadcaster_name: raw.broadcaster.user_name,
    broadcaster_avatar: getAvatarUrl(raw.broadcaster.ava_id),
    app_id: raw.channel_info.app_id,
    rtc_channel_token: raw.channel_info.rtc_channel_token,
    rtm_channel_token: raw.channel_info.rtm_channel_token || null,
    // Agora の user_count は配信者を含むチャンネル参加人数。
    // UI の「視聴者数」では配信者本人を除外し、0 未満にならないよう補正する。
    viewer_count: Math.max(0, (raw.channel_info.user_count ?? 0) - 1),
    point_per_minute: raw.channel_info.point_per_minute ?? null,
    is_free_watching: raw.broadcaster.is_free_watching ?? false,
    thumbnail_url: thumbnailId ? getLiveThumbnailUrl(thumbnailId) : null,
    recording_id: recordingId,
    buzz_file_id: buzzFileId,
    has_lovense: raw.broadcaster.has_lovense ?? false,
    earned_badge_counts: raw.broadcaster.earned_badge_counts ?? {},
    is_standby: isStandby,
    broadcaster_age: raw.broadcaster.age,
    broadcaster_region: raw.broadcaster.region,
    abt: raw.broadcaster.abt,
    bust_size: raw.broadcaster.bust_size,
    average_score: raw.broadcaster.average_score,
    review_count: raw.broadcaster.review_count,
  };
};

export const liveChannelsQueryOptions = () => {
  return queryOptions({
    queryKey: queryKeys.live.channels(),
    queryFn: async () => {
      const raw = await apiClient<SecondAppsLiveChannelsApiResponse>(
        SECOND_APPS_GET_LIVE_CHANNELS,
        { call_type: "live" },
      );
      const result = {
        in_live_list: (raw.in_live_list ?? []).map((ch) =>
          toChannel(ch, false),
        ),
        standby_list: (raw.standby_list ?? []).map((ch) => toChannel(ch, true)),
      } satisfies LiveChannelsResponse;

      return result;
    },
    staleTime: 0,
    refetchInterval: 5_000,
  });
};

export const getRtmLoginAuthMutationOptions = () => {
  return {
    mutationFn: () => apiClient<RtmLoginAuthResponse>(GET_RTM_LOGIN_AUTH),
  };
};

export const getPointQueryOptions = () => {
  return queryOptions({
    queryKey: queryKeys.user.point(),
    queryFn: () => apiClient<GetPointResponse>(GET_POINT),
    staleTime: 0,
  });
};

export const payLivePointMutationOptions = () => {
  return {
    mutationFn: (params: PayLivePointRequest) =>
      apiClient<PayLivePointResponse>(PAY_SECOND_LIVE_POINT, {
        broadcaster_id: params.broadcaster_id,
        duration: params.duration,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.point() });
    },
  };
};

/**
 * ライブ視聴後に配信者へギフト（スタンプギフト）を送付する。
 * 通話後ギフトと同じ send_message_from_web 経路（is_sticker / is_gift）。
 * content は `${categoryId}_${name}` 形式。
 */
export const sendGiftMessageMutationOptions = () => {
  return {
    mutationFn: (params: { userId: string; rcvId: string; content: string }) =>
      apiClient<unknown>(SEND_MESSAGE_FROM_WEB, {
        user_id: params.userId,
        rcv_id: params.rcvId,
        content: params.content,
        is_sticker: true,
        is_gift: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile.detail() });
      queryClient.invalidateQueries({ queryKey: queryKeys.user.point() });
    },
  };
};

/**
 * 「深田えいみコラボ」消費ptゲージの集計API。視聴者全体の合算pt・自分の貢献・
 * 節目(閾値・報酬名・報酬画像)をBE側で集計した値として返す。
 * ゲージの実数値(totalPt/myPt)はこのポーリング結果でしか更新されない
 * （自分の送信直後に出る「+◯◯pt」は演出のみで数値には反映されない。
 * useOnedariPrizeGauge.ts の registerContribution を参照）ため、1秒間隔を維持する。
 */
export const livePresentMenuSummaryQueryOptions = (
  channelId: string,
  enabled: boolean,
) => {
  return queryOptions({
    queryKey: queryKeys.live.presentMenuSummary(channelId),
    queryFn: () =>
      apiClient<GetLivePresentMenuSummaryResponse>(
        GET_LIVE_PRESENT_MENU_SUMMARY,
        {
          application_id: getCurrentApplicationId(),
          channel_id: channelId,
        },
      ),
    enabled,
    staleTime: 0,
    refetchInterval: 1000,
  });
};

export const uncompletedLovenseLogQueryOptions = (
  broadcasterId: string,
  enabled: boolean,
) => {
  return queryOptions({
    queryKey: queryKeys.live.uncompletedLog(broadcasterId),
    queryFn: () =>
      apiClient<GetUncompletedLovenseLogResponse>(
        GET_UNCOMPLETED_LOVENSE_LOG_BY_ROOM,
        { female_id: broadcasterId },
      ),
    enabled,
    staleTime: 0,
    refetchInterval: 1000,
  });
};

/**
 * スパチャメニュー取得（深田えいみ配信限定。対象外の配信者・審査対策ユーザーには
 * 空の menu_list が返る＝エラーではない）。application_id は apiClient が自動注入しないため必須で渡す。
 * メニューは配信中に変わらない前提のため staleTime: Infinity（enterLive と同じ寿命でキャッシュする）。
 */
export const superChatMenuQueryOptions = (
  partnerId: string,
  enabled: boolean,
) => {
  return queryOptions({
    queryKey: queryKeys.live.superChatMenu(partnerId),
    queryFn: () =>
      apiClient<GetSuperChatMenuListResponse>(GET_SUPER_CHAT_MENU_LIST, {
        partner_id: partnerId,
        application_id: getCurrentApplicationId(),
      }),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
  });
};
